import { Box, Button, color, Dialog, Header, IconButton, Text, config, toRem } from 'folds';
import { Avatar, roomAvatarShape } from '$components/avatar';
import { useMemo, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { EventType, type Room } from '$types/matrix-sdk';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useRoomName } from '$hooks/useRoomMeta';
import { useCallEmbed } from '$hooks/useCallEmbed';
import { getMxIdLocalPart } from '$utils/matrix';
import { getAvatarUrl, getMemberDisplayName, getRoomAvatarUrl } from '$utils/room/display';
import { webRTCSupported } from '$utils/rtc';
import { useRoomNavigate } from '$hooks/useRoomNavigate';
import * as Sentry from '@sentry/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  autoJoinCallIntentAtom,
  callSoundBlockedAtom,
  incomingCallAtom,
  mutedCallRoomIdAtom,
  type IncomingCall,
} from '$state/callEmbed';
import { livekitJsCallAtom } from '$state/livekitJsCall';
import { nativeCallAtom, selectActiveCallSessionIncludingNative } from '$state/nativeCall';
import { createDebugLogger } from '$utils/debugLogger';
import { dismissSystemCallNotifications } from '$features/call/callNotificationBridge';
import { getIncomingCallBlockers } from '@sableclient/matrixrtc';
import { RoomAvatar } from '$components/room-avatar';
import { UserAvatar } from '$components/user-avatar';
import {
  composerIcon,
  menuIcon,
  Phone,
  X,
  Hash,
  VideoCamera,
  User,
  sizedIcon,
} from '$components/icons/phosphor';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';
import * as css from './IncomingCallModal.css';

const debugLog = createDebugLogger('IncomingCall');

type IncomingCallInternalProps = {
  room: Room;
  incomingCall: IncomingCall;
  onClose: () => void;
};

export function IncomingCallInternal({ room, incomingCall, onClose }: IncomingCallInternalProps) {
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const roomName = useRoomName(room);
  const callEmbed = useCallEmbed();
  const livekitJsCall = useAtomValue(livekitJsCallAtom);
  const nativeCall = useAtomValue(nativeCallAtom);
  const { navigateRoom } = useRoomNavigate();
  const roomAvatarUrl = getRoomAvatarUrl(mx, room, 96, useAuthentication);
  const setAutoJoinIntent = useSetAtom(autoJoinCallIntentAtom);
  const setMutedRoomId = useSetAtom(mutedCallRoomIdAtom);
  const setCallSoundBlocked = useSetAtom(callSoundBlockedAtom);
  const callSoundBlocked = useAtomValue(callSoundBlockedAtom);
  const callerDisplayName =
    getMemberDisplayName(room, incomingCall.senderId) ??
    getMxIdLocalPart(incomingCall.senderId) ??
    incomingCall.senderId;
  const callerAvatarMxc = room.getMember(incomingCall.senderId)?.getMxcAvatarUrl();
  const callerAvatarUrl = getAvatarUrl(mx, callerAvatarMxc, 96, useAuthentication);

  const isRingNotification = incomingCall.notificationType === 'ring';
  const isDirectRing = incomingCall.isDirect && incomingCall.notificationType === 'ring';
  const isVideoIntent = incomingCall.intentKind === 'video';
  // All three engines refuse to start while another call is live, so the modal
  // has to know about all three or answering silently does nothing.
  const activeCall = selectActiveCallSessionIncludingNative(callEmbed, livekitJsCall, nativeCall);
  const inAnotherCall = Boolean(activeCall && activeCall.roomId !== room.roomId);
  const canUseWebRTC = webRTCSupported();
  const myUserId = mx.getSafeUserId();
  const hasCallMemberPermission =
    room.currentState?.maySendStateEvent(EventType.RTCMembership, myUserId) ||
    room.currentState?.maySendStateEvent(EventType.GroupCallMemberPrefix, myUserId) ||
    false;

  const capabilityIssues = useMemo(
    () =>
      getIncomingCallBlockers({
        canUseWebRTC,
        hasCallMemberPermission,
        inAnotherCall,
      }),
    [canUseWebRTC, hasCallMemberPermission, inAnotherCall]
  );

  const canAnswer = capabilityIssues.length === 0;
  const primaryBlockedReason = capabilityIssues[0]?.shortReason;

  const incomingLabel = isRingNotification
    ? isVideoIntent
      ? 'Incoming video call'
      : 'Incoming voice call'
    : 'Incoming room call notification';
  const dismissLabel = isDirectRing ? 'Decline' : 'Ignore';
  const closeLabel = 'Close';
  const showCallerAvatar = incomingCall.isDirect;
  const title = showCallerAvatar ? callerDisplayName : roomName;
  const subtitle = showCallerAvatar ? roomName : callerDisplayName;

  const handleAnswer = () => {
    if (!canAnswer) return;
    setCallSoundBlocked(false);

    debugLog.info('call', 'Incoming call answered', {
      roomId: room.roomId,
      notificationEventId: incomingCall.notificationEventId,
      notificationType: incomingCall.notificationType,
      intent: incomingCall.intentRaw,
    });
    Sentry.metrics.count('sable.call.answered', 1, {
      attributes: {
        type: incomingCall.notificationType,
        dm: String(incomingCall.isDirect),
        intent: incomingCall.intentKind,
      },
    });

    setMutedRoomId(room.roomId);
    setAutoJoinIntent({ roomId: room.roomId, video: isVideoIntent });
    void dismissSystemCallNotifications(room.roomId);
    onClose();
    navigateRoom(room.roomId);
  };

  const handleDeclineOrIgnore = () => {
    setCallSoundBlocked(false);
    const action = isDirectRing ? 'decline' : 'ignore';
    debugLog.info('call', 'Incoming call dismissed', {
      roomId: room.roomId,
      action,
      notificationEventId: incomingCall.notificationEventId,
      notificationType: incomingCall.notificationType,
    });
    Sentry.metrics.count(`sable.call.${action}d`, 1, {
      attributes: {
        type: incomingCall.notificationType,
        dm: String(incomingCall.isDirect),
      },
    });

    setMutedRoomId(room.roomId);
    void dismissSystemCallNotifications(room.roomId);
    onClose();

    if (isDirectRing) {
      void mx.sendRtcDecline(room.roomId, incomingCall.notificationEventId).catch((error) => {
        debugLog.warn('call', 'Failed to send RTC decline event', {
          roomId: room.roomId,
          notificationEventId: incomingCall.notificationEventId,
          error: error instanceof Error ? error.message : String(error),
        });
        Sentry.metrics.count('sable.call.decline.error', 1);
      });
    }
  };

  const handleClose = () => {
    setCallSoundBlocked(false);
    const action = 'ignore';
    debugLog.info('call', 'Incoming call dismissed', {
      roomId: room.roomId,
      action,
      notificationEventId: incomingCall.notificationEventId,
      notificationType: incomingCall.notificationType,
    });
    Sentry.metrics.count(`sable.call.${action}d`, 1, {
      attributes: {
        type: incomingCall.notificationType,
        dm: String(incomingCall.isDirect),
      },
    });

    setMutedRoomId(room.roomId);
    void dismissSystemCallNotifications(room.roomId);
    onClose();
  };

  const handleModalKeyDown = (evt: ReactKeyboardEvent<HTMLDivElement>) => {
    if (evt.key === 'Escape') {
      evt.preventDefault();
      evt.stopPropagation();
      handleClose();
      return;
    }
    if (evt.key === 'Enter' && canAnswer) {
      evt.preventDefault();
      evt.stopPropagation();
      handleAnswer();
    }
  };

  return (
    <Dialog
      variant="Surface"
      onKeyDown={handleModalKeyDown}
      aria-label={`${incomingLabel} in ${roomName}`}
      style={{ width: 'min(340px, calc(100vw - 2rem))', maxWidth: toRem(340) }}
    >
      <Header
        style={{
          padding: `0 ${config.space.S200} 0 ${config.space.S400}`,
          borderBottomWidth: config.borderWidth.B300,
        }}
        variant="Surface"
        size="500"
      >
        <Box grow="Yes">
          <Text size="H4">Incoming Call</Text>
        </Box>
        <IconButton
          size="300"
          onClick={handleClose}
          radii="300"
          aria-label={closeLabel}
          title={closeLabel}
        >
          {composerIcon(X)}
        </IconButton>
      </Header>

      <Box className={css.Content} direction="Column" alignItems="Center">
        <Avatar size="500" shape={roomAvatarShape(incomingCall.isDirect)}>
          {showCallerAvatar ? (
            <UserAvatar
              userId={incomingCall.senderId}
              src={callerAvatarUrl}
              alt={callerDisplayName}
              renderFallback={() => sizedIcon(User, '200', { filled: true })}
            />
          ) : (
            <RoomAvatar
              roomId={room.roomId}
              src={roomAvatarUrl ?? undefined}
              alt={roomName}
              renderFallback={() => sizedIcon(Hash, '200', { filled: true })}
            />
          )}
        </Avatar>

        <Box direction="Column" alignItems="Center" gap="100">
          <Text size="L400" align="Center" truncate>
            {title}
          </Text>
          <Text priority="400" size="T300" align="Center">
            {incomingLabel}
          </Text>
          <Text priority="300" size="T200" align="Center">
            {showCallerAvatar ? `Room: ${subtitle}` : `Caller: ${subtitle}`}
          </Text>
        </Box>

        {capabilityIssues.length > 0 && (
          <Box
            direction="Column"
            gap="100"
            style={{
              width: '100%',
              border: `1px solid ${color.Surface.ContainerLine}`,
              borderRadius: toRem(8),
              padding: config.space.S300,
            }}
          >
            {capabilityIssues.map((issue) => (
              <Text key={issue.id} size="T200" style={{ color: color.Critical.Main }}>
                {issue.message}
              </Text>
            ))}
          </Box>
        )}

        <Box gap="300" style={{ width: '100%' }} justifyContent="Center">
          <Button
            variant="Critical"
            fill="Soft"
            style={{ minWidth: '110px' }}
            onClick={handleDeclineOrIgnore}
            aria-label={dismissLabel === 'Decline' ? 'Decline call' : 'Ignore call notification'}
            autoFocus={!canAnswer}
          >
            <Text size="B400">{dismissLabel}</Text>
          </Button>
          <Button
            fill="Solid"
            variant="Primary"
            style={{ minWidth: '110px' }}
            onClick={handleAnswer}
            disabled={!canAnswer}
            before={menuIcon(isVideoIntent ? VideoCamera : Phone)}
            aria-label={isVideoIntent ? 'Answer video call' : 'Answer voice call'}
            autoFocus={canAnswer}
          >
            <Text size="B400">Answer</Text>
          </Button>
        </Box>
        {!canAnswer && primaryBlockedReason && (
          <Text size="T200" priority="300" align="Center">
            {primaryBlockedReason}
          </Text>
        )}
        {callSoundBlocked && (
          <Text size="T200" style={{ color: color.Warning.Main }} align="Center">
            Call sound was blocked by your browser. Click any call action to re-enable sound.
          </Text>
        )}
      </Box>
    </Dialog>
  );
}

export function IncomingCallModal() {
  const [incomingCall, setIncomingCall] = useAtom(incomingCallAtom);
  const mx = useMatrixClient();
  const room = incomingCall ? mx.getRoom(incomingCall.roomId) : null;

  const close = () => setIncomingCall(null);

  if (!incomingCall || !room) return null;

  return (
    <ModalOverlay requestClose={close} dismissOnClickOutside={false} escapeDeactivates={false}>
      <div>
        <IncomingCallInternal room={room} incomingCall={incomingCall} onClose={close} />
      </div>
    </ModalOverlay>
  );
}
